<template>
    <div class="accounting-balance-sheet-table-view" :class="{ loading: isLoading }">
        <div v-if="isLoading" class="loading-cover">載入中...</div>
        <div class="doc-header">
            <div class="company-name">{{ companyName }}<span v-if="isTrying">（試用）</span></div>
            <div class="doc-name">資產負債表</div>
            <div class="date">{{ queryDateText }}</div>
            <div class="date" style="text-align: right; font-size: 8pt; margin-bottom: 10px">
                <div>製表日期：{{ todayDateTxt }}</div>
                <div v-if="settlementDateTxt" class="no-print">結算日期：{{ settlementDateTxt }}</div>
                <div v-if="closedDateTxt" class="no-print">關帳日期：{{ closedDateTxt }}</div>
            </div>
        </div>
        <div class="table-block">
            <div class="balance-sheet-table">
                <div class="cell-block cell-assets-title">
                    <div class="group">
                        <ul class="item-list">
                            <li class="sub-total">
                                <div class="accno"></div>
                                <div class="name">資產</div>
                                <div class="total">金額</div>
                                <div class="percent">%</div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="cell-block cell-assets-items">
                    <!-- 流動資產 -->
                    <div class="group secondary" v-if="datas.currentAssets.length > 0">
                        <div class="group-title">流動資產</div>
                        <ul class="item-list">
                            <li v-for="(item, index) in datas.currentAssets" :key="`${index}-${item.accno}`">
                                <div class="accno">{{ item.accno }}</div>
                                <div class="name" @click="onClickItem(item)">{{ item.accnam }}</div>
                                <div class="total"><Money :v="item.ntTotalAmount" /></div>
                                <div :class="{ percent: true, negative: item.ntTotalAmount < 0 }">{{ getPercent(item.ntTotalAmount, totals.assets) }} %</div>
                            </li>
                            <li class="sub-total">
                                <div class="accno"></div>
                                <div class="name">流動資產總額</div>
                                <div class="total"><Money :v="totals.currentAssets" /></div>
                                <div :class="{ percent: true, negative: totals.currentAssets < 0 }">{{ getPercent(totals.currentAssets, totals.assets) }} %</div>
                            </li>
                        </ul>
                    </div>
                    <!-- 非流動資產 -->
                    <div class="group secondary" v-if="datas.nonCurrentAssets.length > 0">
                        <div class="group-title" style="margin-top: 20px">非流動資產</div>
                        <ul class="item-list">
                            <li v-for="(item, index) in datas.nonCurrentAssets" :key="`${index}-${item.accno}`">
                                <div class="accno">{{ item.accno }}</div>
                                <div class="name" @click="onClickItem(item)">{{ item.accnam }}</div>
                                <div class="total"><Money :v="item.ntTotalAmount" /></div>
                                <div :class="{ percent: true, negative: item.ntTotalAmount < 0 }">{{ getPercent(item.ntTotalAmount, totals.assets) }} %</div>
                            </li>
                            <li class="sub-total">
                                <div class="accno"></div>
                                <div class="name">非流動資產總額</div>
                                <div class="total"><Money :v="totals.nonCurrentAssets" /></div>
                                <div :class="{ percent: true, negative: totals.nonCurrentAssets < 0 }">{{ getPercent(totals.nonCurrentAssets, totals.assets) }} %</div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="cell-block cell-assets-total">
                    <div class="group">
                        <ul class="item-list">
                            <li class="sub-total">
                                <div class="accno"></div>
                                <div class="name">資產總額</div>
                                <div class="total" :class="{ unequal: !isAssetsAndLiabilitiesEqual }"><Money :v="totals.assets" /></div>
                                <div :class="{ percent: true, negative: totals.assets < 0 }">100.00 %</div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="cell-block cell-liabilities-title">
                    <div class="group">
                        <ul class="item-list">
                            <li class="sub-total">
                                <div class="accno"></div>
                                <div class="name">負債</div>
                                <div class="total">金額</div>
                                <div class="percent">%</div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="cell-block cell-liabilities-items">
                    <!-- 流動負債 -->
                    <div class="group secondary" v-if="datas.currentLiabilities.length > 0">
                        <div class="group-title">流動負債</div>
                        <ul class="item-list">
                            <li v-for="(item, index) in datas.currentLiabilities" :key="`${index}-${item.accno}`">
                                <div class="accno">{{ item.accno }}</div>
                                <div class="name" @click="onClickItem(item)">{{ item.accnam }}</div>
                                <div class="total"><Money :v="item.ntTotalAmount" /></div>
                                <div :class="{ percent: true, negative: item.ntTotalAmount < 0 }">{{ getPercent(item.ntTotalAmount, totals.liabilitiesAndEquity) }} %</div>
                            </li>
                            <li class="sub-total">
                                <div class="accno"></div>
                                <div class="name">流動負債總額</div>
                                <div class="total"><Money :v="totals.currentLiabilities" /></div>
                                <div :class="{ percent: true, negative: totals.currentLiabilities < 0 }">{{ getPercent(totals.currentLiabilities, totals.liabilitiesAndEquity) }} %</div>
                            </li>
                        </ul>
                    </div>
                    <!-- 非流動負債 -->
                    <div class="group secondary" v-if="datas.nonCurrentLiabilities.length > 0">
                        <div class="group-title" style="margin-top: 20px">非流動負債</div>
                        <ul class="item-list">
                            <li v-for="(item, index) in datas.nonCurrentLiabilities" :key="`${index}-${item.accno}`">
                                <div class="accno">{{ item.accno }}</div>
                                <div class="name" @click="onClickItem(item)">{{ item.accnam }}</div>
                                <div class="total"><Money :v="item.ntTotalAmount" /></div>
                                <div :class="{ percent: true, negative: item.ntTotalAmount < 0 }">{{ getPercent(item.ntTotalAmount, totals.liabilitiesAndEquity) }} %</div>
                            </li>
                            <li class="sub-total">
                                <div class="accno"></div>
                                <div class="name">非流動負債總額</div>
                                <div class="total"><Money :v="totals.nonCurrentLiabilities" /></div>
                                <div :class="{ percent: true, negative: totals.nonCurrentLiabilities < 0 }">{{ getPercent(totals.nonCurrentLiabilities, totals.liabilitiesAndEquity) }} %</div>
                            </li>
                        </ul>
                    </div>
                    <div class="group">
                        <ul class="item-list">
                            <li class="sub-total">
                                <div class="accno"></div>
                                <div class="name">負債總額</div>
                                <div class="total"><Money :v="totals.liabilities" /></div>
                                <div :class="{ percent: true, negative: totals.liabilities < 0 }">{{ getPercent(totals.liabilities, totals.liabilitiesAndEquity) }} %</div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="cell-block cell-equity-title">
                    <div class="block-title">權益</div>
                </div>
                <div class="cell-block cell-equity-items">
                    <!-- 權益 -->
                    <div class="group">
                        <div class="group-title">股東權益</div>
                        <ul class="item-list">
                            <li v-for="(item, index) in datas.totalEquity" :key="`${index}-${item.accno}`">
                                <div class="accno">{{ item.accno }}</div>
                                <div class="name" @click="onClickItem(item)">{{ item.accnam }}</div>
                                <div class="total"><Money :v="item.ntTotalAmount" /></div>
                                <div :class="{ percent: true, negative: item.ntTotalAmount < 0 }">{{ getPercent(item.ntTotalAmount, totals.liabilitiesAndEquity) }} %</div>
                            </li>
                        </ul>
                    </div>
                    <div class="group">
                        <ul class="item-list">
                            <li class="sub-total">
                                <div class="accno"></div>
                                <div class="name">權益總額</div>
                                <div class="total"><Money :v="totals.totalEquity" /></div>
                                <div :class="{ percent: true, negative: totals.totalEquity < 0 }">{{ getPercent(totals.totalEquity, totals.liabilitiesAndEquity) }} %</div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="cell-block cell-liabilities-and-equity-total">
                    <div class="group">
                        <ul class="item-list">
                            <li class="sub-total">
                                <div class="accno"></div>
                                <div class="name">負債與權益總計</div>
                                <div class="total" :class="{ unequal: !isAssetsAndLiabilitiesEqual }"><Money :v="totals.liabilitiesAndEquity" /></div>
                                <div :class="{ percent: true, negative: totals.liabilitiesAndEquity < 0 }">100.00 %</div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="no-print">
                <el-alert v-if="!isAssetsAndLiabilitiesEqual" type="error" :closable="false" style="padding-bottom: 12px">
                    <i class="el-icon-warning"></i> <strong>注意！資產總額與負債總額有差異，請做會計科目檢查。</strong>
                </el-alert>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';

export default {
    props: {
        datas: {
            type: Object,
            default: () => ({
                currentAssets: [],
                nonCurrentAssets: [],
                currentLiabilities: [],
                nonCurrentLiabilities: [],
                totalEquity: [],
            }),
        },
        totals: {
            type: Object,
            default: () => ({
                assets: 0,
                currentAssets: 0,
                nonCurrentAssets: 0,
                liabilitiesAndEquity: 0,
                currentLiabilities: 0,
                nonCurrentLiabilities: 0,
                liabilities: 0,
                totalEquity: 0,
            }),
        },
        queryParams: {
            type: Object,
            default: () => ({
                queryDate: null,
                startDate: null,
            }),
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        settlementDateTxt: {
            type: String,
            default: '',
        },
        closedDateTxt: {
            type: String,
            default: '',
        },
    },
    computed: {
        ...mapGetters({ isTrying: 'menu/isTrying' }),
        companyName() {
            return this.$store.getters['menu/companyInfo'].cH_CMP;
        },
        isAssetsAndLiabilitiesEqual() {
            return this.totals.liabilitiesAndEquity === this.totals.assets;
        },
        queryDateText() {
            const START_DATE = moment(+this.startDate);
            const END_DATE = moment(this.queryParams.queryDate);

            return `民國 ${START_DATE.year() - 1911} 年 ${START_DATE.format('MM 月 DD 日')} ～ 民國 ${END_DATE.year() - 1911} 年 ${END_DATE.format('MM 月 DD 日')}`;
        },
        todayDateTxt() {
            return moment().format('YYYY年MM月DD日');
        },
        startDate() {
            if (!this.queryParams.startDate) {
                return moment(this.queryParams.queryDate).startOf('year').format('x');
            }
            return moment(this.queryParams.startDate).format('x');
        },
    },
    methods: {
        onClickItem(item) {
            this.$emit('click', item);
        },
        getPercent(amount, total) {
            return total ? ((amount / total) * 100).toFixed(2) : '0.00';
        },
    },
};
</script>

<style lang="scss">
.accounting-balance-sheet-table-view {
    position: relative;

    &.loading {
        .loading-cover {
            position: absolute;
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: rgba(255,255,255,0.95);
            color: #463cb2;
            font-size: 11pt;
            font-weight: bold;
        }
    }

    .doc-header {
        margin-top: 10px;
        text-align: center;
        padding: 20px 30px 0px 30px;

        @media print {
            margin-top: 30px;
            border-top: 0;
            padding: 0;
            line-height: 130%;
        }

        .company-name {
            font-size: 15pt;
            font-weight: bold;
            @media print {
                font-size: 11pt;
            }
        }
        .doc-name {
            font-size: 15pt;
            font-weight: bold;
            margin: 3px 0;

            @media print {
                margin: 2px 0;
                font-size: 11pt;
            }
        }
        .date {
            font-size: 12pt;
            font-weight: bold;
            @media print {
                font-size: 9pt;
            }
        }
    }

    .table-block {
        padding: 0 30px 30px 30px;
        @media print {
            padding: 0;
        }
    }

    .balance-sheet-table {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 35px auto 35px auto 35px;
        border: 1px solid #000;
        font-size: 12pt;
        line-height: 160%;
        margin-bottom: 50px;

        @media print {
            font-size: 8.5pt;
            padding-bottom: 0px;
        }

        .cell-block {
            border: 1px solid #000;
            padding: 5px 15px;
        }

        /* 資產標題 */
        .cell-assets-title {
            grid-column-start: 1;
            grid-column-end: 2;
            grid-row-start: 1;
            grid-row-end: 2;
        }
        /* 資產項目（流動＆非流動） */
        .cell-assets-items {
            grid-column-start: 1;
            grid-column-end: 2;
            grid-row-start: 2;
            grid-row-end: 5;
        }
        /* 資產總計 */
        .cell-assets-total {
            grid-column-start: 1;
            grid-column-end: 2;
            grid-row-start: 5;
            grid-row-end: 6;
        }
        /* 負債標題 */
        .cell-liabilities-title {
            grid-column-start: 2;
            grid-column-end: 3;
            grid-row-start: 1;
            grid-row-end: 2;
        }
        /* 負債項目（流動＆非流動） */
        .cell-liabilities-items {
            grid-column-start: 2;
            grid-column-end: 3;
            grid-row-start: 2;
            grid-row-end: 3;
        }
        /* 權益標題 */
        .cell-equity-title {
            grid-column-start: 2;
            grid-column-end: 3;
            grid-row-start: 3;
            grid-row-end: 4;
        }
        /* 權益項目 */
        .cell-equity-items {
            grid-column-start: 2;
            grid-column-end: 3;
            grid-row-start: 4;
            grid-row-end: 5;
        }
        /* 負債與權益總計 */
        .cell-liabilities-and-equity-total {
            grid-column-start: 2;
            grid-column-end: 3;
            grid-row-start: 5;
            grid-row-end: 6;
        }

        /* 標題 */
        .block-title {
            font-weight: bold;
        }
        /* 次標題 */
        .group {
            &.secondary {
                margin-left: 20px;
            }
            /* 分類標題 */
            .group-title {
                font-weight: bold;
            }
            /* 項目列表 */
            ul.item-list {
                display: table;
                width: 100%;
                margin-bottom: 20px;

                > li {
                    display: table-row;
                    > .accno {
                        display: table-cell;
                        display: none;
                        width: 120px;
                    }
                    > .name {
                        cursor: pointer;
                        display: table-cell;
                        padding-left: 30px;
                        @media print {
                            padding-left: 0.3cm;
                        }
                        &:hover {
                            color: #463cb2;
                        }
                    }
                    > .total {
                        display: table-cell;
                        text-align: right;
                        width: 200px;
                        @media print {
                            width: 2cm;
                        }

                        &.unequal {
                            color: red;
                        }

                        .money {
                            font-weight: normal;
                            &.negative {
                                color: red;
                            }
                        }
                    }
                    > .percent {
                        display: table-cell;
                        text-align: right;
                        width: 100px;
                        &.negative {
                            color: red;
                        }
                        @media print {
                            width: 1.7cm;
                        }
                    }
                }
                > li.sub-total {
                    > .name {
                        padding-left: 0px;
                        font-weight: bold;
                        cursor: auto;
                    }
                }
            }
        }
    }
}
</style>
